import React, { useEffect, useState } from 'react';
import ProgressBar from './ProgressBar';
import Spinner from '../SalesData/Spinner';
import useFreshdeskTagging from './useFreshdeskTagging';
import { buttonStyle, cardDescriptionStyle, cardStyle, cardTitleStyle, fieldsStyle, fieldStyle, inputStyle, labelStyle } from './styles';

const MAX_RANGE = 50000;
const MAX_LISTED_ERRORS = 50;
const MAX_LISTED_CHANGES = 200;
// A ticket normally takes a second or less, after this it is shown as slow
const SLOW_SECONDS = 15;

const messageStyle = (ok) => ({
    margin: '0.75rem 0 0',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.25rem',
    color: ok ? '#1b6e2d' : '#b3261e',
    backgroundColor: ok ? '#e3f4e7' : '#fde7e5',
});

const secondaryButtonStyle = { ...buttonStyle(false), marginTop: 0, color: '#111', backgroundColor: '#e6e6e6' };

const formatMinutes = (ms) => {
    const minutes = Math.round(ms / 60000);
    return minutes < 1 ? 'mindre än en minut' : `ca ${minutes} min`;
};

const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString('sv-SE');

const Tags = ({ added, removed }) => (
    <>
        {added.map((tag) => <span key={`a-${tag}`} style={{ marginRight: '0.4rem', color: '#1b6e2d' }}>+{tag}</span>)}
        {removed.map((tag) => <span key={`r-${tag}`} style={{ marginRight: '0.4rem', color: '#b3261e' }}>−{tag}</span>)}
    </>
);

// The ticket that is being worked on and for how long, so that it can be seen which ticket a run is stuck on
const CurrentTicket = ({ ticketId, startedAt }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 500);
        return () => clearInterval(timer);
    }, []);

    const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
    const slow = seconds >= SLOW_SECONDS;

    return (
        <div style={{ margin: '0.75rem 0 0', fontSize: '0.9rem', color: slow ? '#b3261e' : 'inherit' }}>
            <Spinner />Kör just nu: <strong>ärende {ticketId}</strong> ({seconds} s)
            {slow && ' – det tar lång tid, Freshdesk kan ha nått sin gräns eller hänger sig. Anropet ges upp efter 150 s.'}
        </div>
    );
};

const LogLine = ({ entry }) => {
    const link = entry.url ? <a href={entry.url} target="_blank" rel="noopener noreferrer">#{entry.ticketId}</a> : <>#{entry.ticketId}</>;
    const found = entry.kind !== 'notFound' && entry.kind !== 'error';

    let text;
    switch (entry.kind) {
        case 'notFound': text = <span style={{ color: '#888' }}>finns inte</span>; break;
        case 'unchanged': text = <span style={{ color: '#888' }}>ingen ändring</span>; break;
        case 'error': text = <span style={{ color: '#b3261e' }}>fel: {entry.error}</span>; break;
        default: text = (
            <>
                <strong>{entry.kind === 'written' ? 'skrivet' : 'skulle skrivas'}</strong>{' '}
                <Tags added={entry.added} removed={entry.removed} />
            </>
        );
    }

    // Order numbers and tags are shown for every ticket that exists, also when nothing is changed. The tags are the ones the ticket has in Freshdesk now.
    return (
        <li style={{ padding: '0.1rem 0' }}>
            <span style={{ color: '#888' }}>{formatTime(entry.at)}</span> {link} {text}
            {found && (
                <span style={{ color: '#666' }}>
                    {' · '}ordernummer: {entry.orderNumbers.length > 0 ? entry.orderNumbers.join(', ') : 'inga'}
                    {' · '}taggar: {entry.tags.length > 0 ? entry.tags.join(', ') : 'inga'}
                </span>
            )}
        </li>
    );
};

const FreshdeskTagging = () => {
    const tagging = useFreshdeskTagging();
    const { running, status, lastDone, resumeAfter, saved } = tagging;

    // The range of the last run that wrote tags is filled in, the first ticket is 1. Writing is never ticked from the start.
    const [from, setFrom] = useState(saved ? String(saved.from) : '1');
    const [to, setTo] = useState(saved ? String(saved.to) : '');
    const [write, setWrite] = useState(false);

    const fromNumber = Number(from);
    const toNumber = Number(to);
    const rangeError = (() => {
        if (!from || !to) return null;
        if (!Number.isInteger(fromNumber) || !Number.isInteger(toNumber) || fromNumber < 1) return 'Ange ärendenummer som heltal från 1.';
        if (toNumber < fromNumber) return 'Till måste vara minst lika stort som Från.';
        if (toNumber - fromNumber + 1 > MAX_RANGE) return `Högst ${MAX_RANGE} ärenden åt gången.`;
        return null;
    })();
    const valid = from !== '' && to !== '' && !rangeError;

    const confirmWrite = (first, last) => !write || window.confirm(
        `Vill du skriva ordertaggar till Freshdesk-ärendena ${first}–${last}? Bara taggar ändras, övriga taggar behålls. Det går inte att ångra med ett klick.`
    );

    const handleStart = (e) => {
        e.preventDefault();
        if (running || !valid || !confirmWrite(fromNumber, toNumber)) return;
        tagging.run(fromNumber, toNumber, write);
    };

    // A run of this session that was stopped or failed is continued with the same range and mode, the counts are kept
    const sessionNext = resumeAfter ? resumeAfter + 1 : tagging.from;
    const sessionResumable = !running && status !== 'succeeded' && tagging.from !== null && tagging.to !== null && sessionNext <= tagging.to;
    // Otherwise the position that is saved in the cookie, from an earlier run that wrote tags, is continued
    const savedNext = saved ? (saved.lastDone ? saved.lastDone + 1 : saved.from) : null;
    const savedResumable = !running && !sessionResumable && saved !== null && savedNext <= saved.to;
    const canResume = sessionResumable || savedResumable;
    const nextTicket = sessionResumable ? sessionNext : savedNext;

    const handleResume = () => {
        if (!canResume) return;

        const range = sessionResumable
            ? { from: tagging.from, to: tagging.to, write: Boolean(tagging.write) }
            : { from: saved.from, to: saved.to, write: true };
        if (range.write && !window.confirm(`Vill du fortsätta att skriva ordertaggar från ärende ${nextTicket} till ${range.to}?`)) return;

        setFrom(String(range.from));
        setTo(String(range.to));
        setWrite(range.write);
        // A saved position that has not got anywhere yet is the same as a fresh start
        tagging.run(range.from, range.to, range.write, sessionResumable || saved.lastDone !== null, sessionResumable ? null : saved.lastDone);
    };

    const total = tagging.from && tagging.to ? tagging.to - tagging.from + 1 : 0;
    const done = lastDone ? lastDone - tagging.from + 1 : 0;
    const elapsed = tagging.startedAt ? Date.now() - tagging.startedAt : 0;
    const remaining = running && tagging.sessionDone > 0 ? (elapsed / tagging.sessionDone) * (total - done) : null;
    const mode = tagging.write ? 'Skriver taggar' : 'Torrkörning';

    return (
        <form onSubmit={handleStart} style={cardStyle(true)}>
            <h3 style={cardTitleStyle}>Tagga Freshdesk-ärenden med ordernummer</h3>
            <p style={cardDescriptionStyle}>
                Går igenom ärenden ett i taget, hittar ordernummer (LS…) i ämnet, beskrivningen och alla svar och anteckningar och sätter taggarna order-ls… på ärendet.
                Bara taggar ändras: övriga taggar behålls och ärenden som redan har rätt taggar skrivs inte, så det går att köra om. Freshdesk uppdaterar däremot ändringsdatum på de ärenden som skrivs.
                Torrkörning är förvald och visar bara vilka ärenden som skulle få taggar. Ange spannet av ärendenummer. Hur långt en körning som skriver taggar har kommit sparas i en cookie, så att den kan fortsättas där den slutade även efter en krasch.
            </p>

            <div style={{ ...fieldsStyle, alignItems: 'flex-end' }}>
                <div style={fieldStyle(true)}>
                    <label htmlFor="freshdesk-tagging-from" style={labelStyle}>Från ärendenummer</label>
                    <input id="freshdesk-tagging-from" type="number" min={1} value={from} placeholder="t.ex. 1" disabled={running} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
                </div>
                <div style={fieldStyle(true)}>
                    <label htmlFor="freshdesk-tagging-to" style={labelStyle}>Till ärendenummer</label>
                    <input id="freshdesk-tagging-to" type="number" min={1} value={to} placeholder="t.ex. 2800" disabled={running} onChange={(e) => setTo(e.target.value)} style={inputStyle} />
                </div>
            </div>

            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 1rem', fontWeight: 400 }}>
                <input type="checkbox" checked={write} disabled={running} onChange={(e) => setWrite(e.target.checked)} style={{ margin: 0 }} />
                Skriv taggarna i Freshdesk (annars bara torrkörning)
            </label>

            {rangeError && <div style={{ ...messageStyle(false), margin: '0 0 0.75rem' }}>{rangeError}</div>}

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="submit" style={{ ...buttonStyle(running || !valid), marginTop: 0 }} disabled={running || !valid}>
                    {write ? 'Tagga ärenden' : 'Starta torrkörning'}
                </button>
                {running && <button type="button" onClick={tagging.stop} style={secondaryButtonStyle}>Avbryt</button>}
                {canResume && (
                    <button type="button" onClick={handleResume} style={secondaryButtonStyle}>
                        Fortsätt från ärende {nextTicket}
                    </button>
                )}
            </div>

            {saved && saved.lastDone !== null && (
                <p style={{ ...cardDescriptionStyle, margin: '0.75rem 0 0' }}>
                    <strong>Senaste uppdatering gjord fram till ärende {saved.lastDone}</strong> (spann {saved.from}–{saved.to}{saved.lastDone >= saved.to ? ', klart' : ''}).
                    Den sparas i en cookie och ändras bara av körningar som skriver taggar, inte av torrkörningar.
                </p>
            )}

            {running && (
                <>
                    <ProgressBar label={mode} current={done} total={total} />
                    {tagging.currentTicket && <CurrentTicket ticketId={tagging.currentTicket} startedAt={tagging.currentStartedAt ?? Date.now()} />}
                    {remaining !== null && <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: '#666' }}>Beräknad tid kvar: {formatMinutes(remaining)}</div>}
                </>
            )}

            {total > 0 && (running || status !== 'idle' || done > 0) && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                    {tagging.changed} {tagging.write ? 'skrivna' : 'skulle skrivas'}, {tagging.unchanged} redan rätt, {tagging.notFound} finns inte, {tagging.withOrderNumbers} med ordernummer, {tagging.errors.length} fel
                </div>
            )}

            {status === 'succeeded' && <div role="status" style={messageStyle(tagging.errors.length === 0)}>Klart: ärende {tagging.from}–{tagging.to} är genomgångna.</div>}
            {status === 'cancelled' && <div role="status" style={messageStyle(false)}>Avbrutet{lastDone ? ` efter ärende ${lastDone}` : ''}. Fortsätt från ärende {nextTicket}.</div>}
            {status === 'failed' && <div role="alert" style={messageStyle(false)}>{tagging.error} Fortsätt från ärende {nextTicket} när felet är åtgärdat.</div>}

            {tagging.log.length > 0 && (
                <details open style={{ marginTop: '0.75rem' }}>
                    <summary>Logg, varje ärende som blivit klart (senaste först)</summary>
                    <ul style={{ margin: '0.5rem 0 0', padding: 0, listStyle: 'none', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {tagging.log.map((entry) => <LogLine key={`${entry.ticketId}-${entry.at}`} entry={entry} />)}
                    </ul>
                </details>
            )}

            {tagging.changes.length > 0 && (
                <details open style={{ marginTop: '0.75rem' }}>
                    <summary>{tagging.write ? 'Skrivna ärenden' : 'Ärenden som skulle skrivas'} ({tagging.changed})</summary>
                    <ul style={{ margin: '0.5rem 0 0', padding: 0, listStyle: 'none', fontSize: '0.8rem', maxHeight: '16rem', overflowY: 'auto' }}>
                        {tagging.changes.slice(0, MAX_LISTED_CHANGES).map((change) => (
                            <li key={change.ticketId} style={{ padding: '0.15rem 0' }}>
                                <a href={change.url} target="_blank" rel="noopener noreferrer">#{change.ticketId}</a>{' '}
                                <Tags added={change.added} removed={change.removed} />
                            </li>
                        ))}
                    </ul>
                    {tagging.changes.length > MAX_LISTED_CHANGES && <div style={{ fontSize: '0.8rem' }}>…och fler, de senaste {MAX_LISTED_CHANGES} visas.</div>}
                </details>
            )}

            {tagging.errors.length > 0 && (
                <details style={{ marginTop: '0.75rem' }}>
                    <summary>Fel ({tagging.errors.length}), kör om dem med ett eget spann</summary>
                    <ul style={{ margin: '0.5rem 0 0', fontSize: '0.8rem' }}>
                        {tagging.errors.slice(0, MAX_LISTED_ERRORS).map((e) => <li key={e.ticketId}>#{e.ticketId}: {e.error}</li>)}
                    </ul>
                    {tagging.errors.length > MAX_LISTED_ERRORS && <div style={{ fontSize: '0.8rem' }}>…och {tagging.errors.length - MAX_LISTED_ERRORS} fler.</div>}
                </details>
            )}
        </form>
    );
};

export default FreshdeskTagging;
